import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../hooks/useSubscription";
import SubscriptionRequiredModal from "./SubscriptionRequiredModal";
import api from "../services/api";

interface SubscriptionGuardProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
  showModal?: boolean;
}

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
  children,
  requireSubscription = false,
  showModal = true,
}) => {
  const { t } = useTranslation(["common"]);
  const { hasActiveSubscription, isLoading } = useSubscription();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only show modal if subscription is required and user doesn't have active subscription
    if (requireSubscription && !isLoading && !hasActiveSubscription) {
      if (showModal) {
        setShowSubscriptionModal(true);
      }
    } else {
      setShowSubscriptionModal(false);
    }
  }, [requireSubscription, isLoading, hasActiveSubscription, showModal]);

  const handleLogout = async () => {
    try {
      await api.auth.logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error logging out:", error);
      // Still redirect on error
      navigate("/login", { replace: true });
    }
  };

  const handleCloseModal = () => {
    setShowSubscriptionModal(false);
  };

  // If subscription is required and user doesn't have one, block access
  if (requireSubscription && !isLoading && !hasActiveSubscription) {
    return (
      <>
        <SubscriptionRequiredModal
          isOpen={showSubscriptionModal}
          onClose={handleCloseModal}
          onLogout={handleLogout}
        />
        {/* Optionally render children with overlay if you want to show the content but block interaction */}
        <div
          style={{
            filter: showSubscriptionModal ? "blur(3px)" : "none",
            pointerEvents: showSubscriptionModal ? "none" : "auto",
          }}
        >
          {children}
        </div>
      </>
    );
  }

  // If subscription check is loading, show loading state
  if (requireSubscription && isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          color: "#666",
          fontSize: "16px",
        }}
      >
        {t("common:subscription.checkingStatus")}
      </div>
    );
  }

  // User has subscription or subscription not required, show children normally
  return <>{children}</>;
};

export default SubscriptionGuard;
